/**
 * @file delivery-quote-meta.ts
 * @description Реестры расчёта доставки (phase19.12 шаг 5): причины отказа и
 * метаданные тарифов СДЭК.
 *
 * Реестр состояний карточки «Доставка по заказу» удалён вместе с самой
 * карточкой (phase19.24 шаг 5.2 §5): состояние блока «Доставка» считает
 * lib/delivery-block-state.ts, и второй набор состояний разъезжался бы с ним.
 *
 * Всё справочное — данными, а не каскадом if: новая причина/тариф/состояние =
 * запись в реестре, а не ветка в компоненте.
 */

import type { DeliveryQuoteOption, DeliveryQuoteResult } from "./api-shop-carriers";

// ── Причины, по которым расчёт не выполнен ─────────────────────────────────

/** Что предложить менеджеру рядом с текстом причины. */
export type QuoteReasonAction =
  | { kind: "carrier-settings" }
  | { kind: "integrations" }
  | { kind: "retry" }
  | { kind: "focus"; field: "city" | "weight" };

export interface QuoteReasonMeta {
  severity: "info" | "warning" | "error";
  title: string;
  text: string;
  actionLabel?: string;
  action?: QuoteReasonAction;
}

/** Коды приходят от сервера в `DeliveryQuoteResult.reason`. */
export const QUOTE_REASON_META: Record<string, QuoteReasonMeta> = {
  carrier_not_configured: {
    severity: "warning",
    title: "СДЭК не настроен для магазина",
    text: "Подключите перевозчика и заполните параметры расчёта — без них тарифы запросить не у кого.",
    actionLabel: "Открыть настройки СДЭК",
    action: { kind: "carrier-settings" },
  },
  integration_not_connected: {
    severity: "warning",
    title: "Ключи СДЭК не подключены",
    text: "Доступ к API заводит платформенный администратор в разделе «Интеграции».",
    actionLabel: "Открыть «Интеграции»",
    action: { kind: "integrations" },
  },
  sender_location_missing: {
    severity: "warning",
    title: "Не задана точка отправления",
    text: "Укажите город и адрес отправления в настройках перевозчика — СДЭК считает только от конкретной точки.",
    actionLabel: "Открыть настройки",
    action: { kind: "carrier-settings" },
  },
  destination_missing: {
    severity: "warning",
    title: "Не удалось определить город получателя",
    text: "Укажите город или выберите пункт выдачи — по одному адресу строкой тариф не считается.",
    actionLabel: "Указать город",
    action: { kind: "focus", field: "city" },
  },
  empty_package: {
    severity: "warning",
    title: "Вес места — 0 г",
    text: "Укажите вес хотя бы одного места: по нулевому весу перевозчик тариф не вернёт.",
    actionLabel: "Указать вес",
    action: { kind: "focus", field: "weight" },
  },
  carrier_unavailable: {
    severity: "error",
    title: "СДЭК сейчас не отвечает",
    text: "Это временно. Попробуйте ещё раз через минуту, а если срочно — согласуйте стоимость с клиентом вручную.",
    actionLabel: "Повторить",
    action: { kind: "retry" },
  },
  no_tariffs: {
    severity: "warning",
    title: "СДЭК не вернул ни одного тарифа",
    text: "Для этого направления, веса или габаритов подходящих тарифов нет. Проверьте адрес и размеры мест.",
    actionLabel: "Повторить",
    action: { kind: "retry" },
  },
};

const UNKNOWN_REASON: QuoteReasonMeta = {
  severity: "error",
  title: "Расчёт не выполнен",
  text: "Перевозчик не объяснил причину. Проверьте настройки СДЭК и повторите расчёт.",
  actionLabel: "Повторить",
  action: { kind: "retry" },
};

/** Причина → человеческий текст; неизвестный код не роняет экран. */
export function resolveQuoteReason(reason: string | undefined): QuoteReasonMeta {
  if (!reason) return UNKNOWN_REASON;
  return QUOTE_REASON_META[reason] ?? UNKNOWN_REASON;
}

/**
 * Код причины «расчёт не выполнен» для блока «Доставка» (ревью 5.2).
 *
 * Окно расчёта знает, почему тарифов нет, а объяснять пустую стоимость
 * приходится блоку — иначе менеджер закрывает шторку и видит форму без всякой
 * пометки. `null` — расчёт удался (или его ещё не запускали).
 *
 * Упавший запрос считаем «перевозчик не отвечает»: это единственное, что о нём
 * известно, а реестр состояний относит незнакомый код к «повторите позже».
 */
export function quoteFailureReason(
  quote: DeliveryQuoteResult | null,
  error: string | null,
): string | null {
  if (quote) {
    if (quote.available && quote.options.length > 0) return null;
    return quote.reason ?? "no_tariffs";
  }
  return error ? "carrier_unavailable" : null;
}

// ── Тарифы СДЭК ────────────────────────────────────────────────────────────

/** Известные тарифы: код delivery_mode + доставка до двери или до ПВЗ. */
export const SDEK_TARIFF_META: Record<number, { deliveryMode: number; toDoor: boolean }> = {
  136: { deliveryMode: 4, toDoor: false }, // склад-склад
  137: { deliveryMode: 3, toDoor: true }, // склад-дверь
  138: { deliveryMode: 2, toDoor: false }, // дверь-склад
  139: { deliveryMode: 1, toDoor: true }, // дверь-дверь
  368: { deliveryMode: 4, toDoor: false }, // экономичная посылка склад-склад
  378: { deliveryMode: 3, toDoor: true }, // экономичная посылка склад-дверь
};

/**
 * Тариф до двери? Для незнакомых кодов смотрим на название: у СДЭК вторая
 * часть названия — точка вручения («…-дверь»).
 */
export function isToDoorTariff(option: DeliveryQuoteOption): boolean {
  const known = SDEK_TARIFF_META[option.tariffCode];
  if (known) return known.toDoor;
  return /дверь\s*$/iu.test(option.tariffName.trim());
}

/** delivery_mode для заявки; для незнакомого тарифа решает сервер (null). */
export function tariffDeliveryMode(option: DeliveryQuoteOption): number | null {
  return SDEK_TARIFF_META[option.tariffCode]?.deliveryMode ?? null;
}
